import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { api, getErrorMessage } from "@/lib/api";
import { formatFileSize } from "@/lib/format";
import {
  Share2, Plus, Trash2, X, Check, Loader2, FileText,
  CheckCircle, Ban, ChevronRight, ChevronDown, Search, GripVertical,
  Type, MessageSquare, FolderOpen, Folder, Link,
} from "lucide-react";
import { toast } from "sonner";
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors,
} from "@dnd-kit/core";
import {
  SortableContext, verticalListSortingStrategy, useSortable, arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

// ── Composants réutilisables ───────────────────────────────────────────────

function StatusBadge({ status }) {
  const map = {
    draft:    { label: "Brouillon",  cls: "bg-zinc-100 border-zinc-300 text-zinc-600" },
    active:   { label: "Actif",      cls: "bg-emerald-50 border-emerald-300 text-emerald-700" },
    inactive: { label: "Désactivé",  cls: "bg-amber-50 border-amber-300 text-amber-700" },
    expired:  { label: "Expiré",     cls: "bg-red-50 border-red-300 text-red-700" },
  };
  const { label, cls } = map[status] || map.draft;
  return <span className={`text-[10px] font-mono px-2 py-0.5 border ${cls}`}>{label}</span>;
}

function SortableDocRow({ doc, onRemove }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: doc.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.4 : 1 };
  const fname = doc.classified_filename || doc.original_filename;
  return (
    <tr ref={setNodeRef} style={style} data-testid={`share-doc-${doc.id}`} className="hover:bg-zinc-50 transition-colors">
      <td className="pl-3 pr-1 py-2 w-6">
        <button {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing text-zinc-300 hover:text-zinc-500 touch-none" tabIndex={-1}>
          <GripVertical size={14} strokeWidth={1.5} />
        </button>
      </td>
      <td className="px-3 py-2">
        <div className="flex items-center gap-2 min-w-0">
          <FileText size={13} strokeWidth={1.5} className="text-[#DC2626] shrink-0" />
          <span className="truncate text-sm">{fname}</span>
        </div>
      </td>
      <td className="px-3 py-2 text-right font-mono text-zinc-500 text-xs">{doc.page_count ?? "—"}</td>
      <td className="px-3 py-2 text-right font-mono text-zinc-500 text-xs">{doc.size_bytes != null ? formatFileSize(doc.size_bytes) : "—"}</td>
      <td className="px-3 py-2 text-center">
        <button onClick={() => onRemove(doc.id)} className="inline-flex items-center gap-1 text-xs border border-zinc-200 text-zinc-500 hover:border-red-300 hover:text-red-600 px-2 py-1 transition-colors">
          <Trash2 size={11} strokeWidth={1.5} /> Supprimer
        </button>
      </td>
    </tr>
  );
}

function SortableTextRow({ item, onRemove, onUpdate }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(item.content || "");
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.4 : 1 };
  const isSection = item.item_type === "section";
  const save = async () => {
    const t = text.trim();
    if (t && t !== item.content) await onUpdate(item.id, t);
    else setText(item.content || "");
    setEditing(false);
  };
  return (
    <tr ref={setNodeRef} style={style} data-testid={`share-text-${item.id}`} className={isSection ? "bg-zinc-50/80" : ""}>
      <td className="pl-3 pr-1 py-1.5 w-6">
        <button {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing text-zinc-300 hover:text-zinc-500 touch-none" tabIndex={-1}>
          <GripVertical size={14} strokeWidth={1.5} />
        </button>
      </td>
      <td colSpan={3} className="px-3 py-1.5">
        {editing ? (
          <input autoFocus type="text" value={text} onChange={e => setText(e.target.value)}
            onBlur={save} onKeyDown={e => { if (e.key === "Enter") save(); if (e.key === "Escape") { setText(item.content || ""); setEditing(false); }}}
            className="w-full border border-zinc-300 px-2 py-1 text-sm focus:border-[#002FA7] outline-none" />
        ) : (
          <span onClick={() => setEditing(true)} title="Cliquer pour modifier"
            className={`cursor-text select-none block ${isSection ? "text-xs font-semibold uppercase tracking-[0.12em] text-zinc-600" : "text-xs text-zinc-400"}`}>
            {item.content || (isSection ? "— Section sans titre —" : "— Commentaire vide —")}
          </span>
        )}
      </td>
      <td className="px-3 py-1.5 text-center">
        <button onClick={() => onRemove(item.id)} className="inline-flex items-center gap-1 text-xs border border-zinc-200 text-zinc-500 hover:border-red-300 hover:text-red-600 px-2 py-1 transition-colors">
          <Trash2 size={11} strokeWidth={1.5} /> Supprimer
        </button>
      </td>
    </tr>
  );
}

function todayISO() { return new Date().toISOString().slice(0, 10); }

// ── Page principale ────────────────────────────────────────────────────────

export default function Shares() {
  const [shares, setShares] = useState([]);
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedColls, setExpandedColls] = useState(new Set());
  const [selection, setSelection] = useState({ type: null, id: null }); // type: 'share'|'collection'

  // Création partage
  const [showCreateShare, setShowCreateShare] = useState(false);
  const [newShareTitle, setNewShareTitle] = useState("");
  const [newShareExp, setNewShareExp] = useState("");
  const [creatingShare, setCreatingShare] = useState(false);

  // Création collection
  const [showCreateColl, setShowCreateColl] = useState(false);
  const [newCollTitle, setNewCollTitle] = useState("");
  const [newCollExp, setNewCollExp] = useState("");
  const [creatingColl, setCreatingColl] = useState(false);

  const selectedShare = useMemo(() => selection.type === "share" ? shares.find(s => s.id === selection.id) || null : null, [shares, selection]);
  const selectedColl  = useMemo(() => selection.type === "collection" ? collections.find(c => c.id === selection.id) || null : null, [collections, selection]);

  const loadAll = useCallback(async () => {
    const [sr, cr] = await Promise.all([api.get("/shares"), api.get("/collections")]);
    setShares(sr.data); setCollections(cr.data);
  }, []);

  useEffect(() => { loadAll().finally(() => setLoading(false)); }, [loadAll]);

  const selectShare = useCallback((id) => {
    setSelection({ type: "share", id });
    if (id) api.get(`/shares/${id}`).then(r => setShares(p => p.map(s => s.id === id ? { ...s, ...r.data } : s))).catch(() => {});
  }, []);

  const selectCollection = useCallback((id) => {
    setSelection({ type: "collection", id });
    if (id) api.get(`/collections/${id}`).then(r => setCollections(p => p.map(c => c.id === id ? { ...c, ...r.data } : c))).catch(() => {});
  }, []);

  const toggleExpand = useCallback((id) => {
    setExpandedColls(p => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }, []);

  // ── Créer un partage
  const createShare = async () => {
    if (!newShareTitle.trim() || !newShareExp) { toast.error("Titre et date requis."); return; }
    setCreatingShare(true);
    try {
      const r = await api.post("/shares", { title: newShareTitle.trim(), expiration_date: newShareExp });
      setShares(p => [r.data, ...p]);
      setShowCreateShare(false); setNewShareTitle(""); setNewShareExp("");
      selectShare(r.data.id);
      toast.success("Partage créé.");
    } catch (err) { toast.error(getErrorMessage(err)); }
    finally { setCreatingShare(false); }
  };

  // ── Créer une collection
  const createCollection = async () => {
    if (!newCollTitle.trim() || !newCollExp) { toast.error("Titre et date requis."); return; }
    setCreatingColl(true);
    try {
      const r = await api.post("/collections", { title: newCollTitle.trim(), expiration_date: newCollExp });
      setCollections(p => [r.data, ...p]);
      setShowCreateColl(false); setNewCollTitle(""); setNewCollExp("");
      selectCollection(r.data.id);
      toast.success("Collection créée.");
    } catch (err) { toast.error(getErrorMessage(err)); }
    finally { setCreatingColl(false); }
  };

  return (
    <div className="flex min-h-0 overflow-hidden" style={{ height: "100vh" }}>
      {/* ── Explorateur ── */}
      <aside className="w-[300px] shrink-0 border-r border-zinc-200 bg-white flex flex-col overflow-hidden" data-testid="shares-explorer">
        <div className="flex-1 overflow-y-auto app-scroll">
          {loading ? (
            <div className="p-6 text-sm text-zinc-500">Chargement…</div>
          ) : (
            <>
              {/* ── Section Collections */}
              <div className="px-4 py-2.5 border-b border-zinc-100 flex items-center justify-between sticky top-0 bg-white z-10">
                <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 font-mono">Collections · {collections.length}</span>
                <button data-testid="create-coll-btn" onClick={() => { setShowCreateColl(v => !v); setShowCreateShare(false); }}
                  className="inline-flex items-center gap-1 text-xs font-medium text-white bg-[#002FA7] hover:bg-[#00227A] px-2.5 py-1 transition-colors">
                  <Plus size={11} strokeWidth={2} /> Nouvelle
                </button>
              </div>

              {showCreateColl && (
                <div className="px-3 py-3 border-b border-zinc-100 bg-blue-50/40 space-y-2">
                  <input data-testid="new-coll-title" type="text" value={newCollTitle} onChange={e => setNewCollTitle(e.target.value)}
                    placeholder="Titre de la collection…" className="w-full bg-white border border-zinc-300 px-2.5 py-1.5 text-sm focus:border-[#002FA7] outline-none" />
                  <div className="flex gap-2">
                    <input data-testid="new-coll-exp" type="date" value={newCollExp} min={todayISO()} onChange={e => setNewCollExp(e.target.value)}
                      className="flex-1 bg-white border border-zinc-300 px-2 py-1.5 text-sm focus:border-[#002FA7] outline-none" />
                    <button onClick={createCollection} disabled={creatingColl} className="inline-flex items-center gap-1 text-xs font-medium text-white bg-[#002FA7] px-2.5 py-1.5 disabled:opacity-50">
                      {creatingColl ? <Loader2 size={11} className="animate-spin" /> : <Check size={11} />}
                    </button>
                    <button onClick={() => setShowCreateColl(false)} className="p-1.5 text-zinc-400 hover:text-zinc-700"><X size={13} /></button>
                  </div>
                </div>
              )}

              {collections.length === 0 && !showCreateColl && (
                <div className="px-4 py-3 text-xs text-zinc-400">Aucune collection.</div>
              )}

              {collections.map(c => (
                <div key={c.id}>
                  <button data-testid={`coll-item-${c.id}`}
                    onClick={() => { toggleExpand(c.id); selectCollection(c.id); }}
                    className={`w-full text-left px-3 py-2.5 border-b border-zinc-100 transition-colors flex items-center gap-2 ${
                      selection.type === "collection" && selection.id === c.id ? "bg-[#002FA7]/5 border-l-2 border-l-[#002FA7]" : "hover:bg-zinc-50"
                    }`}>
                    {expandedColls.has(c.id)
                      ? <FolderOpen size={14} strokeWidth={1.5} className="text-[#002FA7] shrink-0" />
                      : <Folder size={14} strokeWidth={1.5} className="text-zinc-400 shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm truncate">{c.title}</span>
                        <StatusBadge status={c.status} />
                      </div>
                      <div className="text-[10px] font-mono text-zinc-400">{c.share_count} dossier(s)</div>
                    </div>
                    {expandedColls.has(c.id) ? <ChevronDown size={12} strokeWidth={1.5} className="text-zinc-300 shrink-0" /> : <ChevronRight size={12} strokeWidth={1.5} className="text-zinc-300 shrink-0" />}
                  </button>

                  {/* Partages de la collection */}
                  {expandedColls.has(c.id) && (c.shares || []).map(s => (
                    <button key={s.id} data-testid={`coll-share-${s.id}`}
                      onClick={e => { e.stopPropagation(); selectShare(s.id); }}
                      className={`w-full text-left pl-9 pr-3 py-2 border-b border-zinc-100 transition-colors flex items-center gap-2 ${
                        selection.type === "share" && selection.id === s.id ? "bg-[#002FA7]/5 border-l-2 border-l-[#002FA7]" : "hover:bg-zinc-50"
                      }`}>
                      <Share2 size={12} strokeWidth={1.5} className="text-zinc-400 shrink-0" />
                      <span className="text-sm truncate flex-1">{s.title}</span>
                      <StatusBadge status={s.status} />
                    </button>
                  ))}
                </div>
              ))}

              {/* ── Section Partages */}
              <div className="px-4 py-2.5 border-b border-zinc-100 flex items-center justify-between sticky top-0 bg-white z-10 mt-2">
                <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 font-mono">Partages · {shares.filter(s => !s.in_collection).length}</span>
                <button data-testid="create-share-btn" onClick={() => { setShowCreateShare(v => !v); setShowCreateColl(false); }}
                  className="inline-flex items-center gap-1 text-xs font-medium text-white bg-[#002FA7] hover:bg-[#00227A] px-2.5 py-1 transition-colors">
                  <Plus size={11} strokeWidth={2} /> Nouveau
                </button>
              </div>

              {showCreateShare && (
                <div className="px-3 py-3 border-b border-zinc-100 bg-blue-50/40 space-y-2" data-testid="create-share-form">
                  <input data-testid="new-share-title" type="text" value={newShareTitle} onChange={e => setNewShareTitle(e.target.value)}
                    placeholder="Titre du partage…" className="w-full bg-white border border-zinc-300 px-2.5 py-1.5 text-sm focus:border-[#002FA7] outline-none" />
                  <div className="flex gap-2">
                    <input data-testid="new-share-exp-date" type="date" value={newShareExp} min={todayISO()} onChange={e => setNewShareExp(e.target.value)}
                      className="flex-1 bg-white border border-zinc-300 px-2 py-1.5 text-sm focus:border-[#002FA7] outline-none" />
                    <button data-testid="create-share-submit" onClick={createShare} disabled={creatingShare} className="inline-flex items-center gap-1 text-xs font-medium text-white bg-[#002FA7] px-2.5 py-1.5 disabled:opacity-50">
                      {creatingShare ? <Loader2 size={11} className="animate-spin" /> : <Check size={11} />}
                    </button>
                    <button onClick={() => setShowCreateShare(false)} className="p-1.5 text-zinc-400 hover:text-zinc-700"><X size={13} /></button>
                  </div>
                </div>
              )}

              {shares.filter(s => !s.in_collection).length === 0 && (
                <div className="px-4 py-3 text-xs text-zinc-400">Aucun partage libre.</div>
              )}

              {shares.filter(s => !s.in_collection).map(s => (
                <button key={s.id} data-testid={`share-item-${s.id}`}
                  onClick={() => selectShare(s.id)}
                  className={`w-full text-left px-3 py-2.5 border-b border-zinc-100 transition-colors flex items-center gap-2 ${
                    selection.type === "share" && selection.id === s.id ? "bg-[#002FA7]/5 border-l-2 border-l-[#002FA7]" : "hover:bg-zinc-50"
                  }`}>
                  <Share2 size={13} strokeWidth={1.5} className="text-zinc-400 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm truncate">{s.title}</span>
                      <StatusBadge status={s.status} />
                    </div>
                    <div className="text-[10px] font-mono text-zinc-400">
                      Exp. {new Date(s.expiration_date).toLocaleDateString("fr-FR")} · {s.document_count} fichier(s)
                    </div>
                  </div>
                  <ChevronRight size={12} strokeWidth={1.5} className="text-zinc-300 shrink-0" />
                </button>
              ))}
            </>
          )}
        </div>
      </aside>

      {/* ── Panneau de détail ── */}
      <div className="flex-1 overflow-hidden">
        {selection.type === "share" && selectedShare && (
          <SharePanel
            share={selectedShare}
            shares={shares}
            setShares={setShares}
            onClose={() => setSelection({ type: null, id: null })}
          />
        )}
        {selection.type === "collection" && selectedColl && (
          <CollectionPanel
            collection={selectedColl}
            collections={collections}
            setCollections={setCollections}
            shares={shares}
            onClose={() => setSelection({ type: null, id: null })}
          />
        )}
        {!selection.id && (
          <div className="flex-1 h-full flex items-center justify-center text-center">
            <div>
              <Share2 size={40} strokeWidth={1} className="text-zinc-200 mx-auto" />
              <div className="mt-3 text-sm text-zinc-400">Sélectionnez un partage ou une collection.</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// Panneau Partage
// ══════════════════════════════════════════════════════════════════════════════

function SharePanel({ share, shares, setShares, onClose }) {
  const selectedId = share.id;
  const [editTitle, setEditTitle] = useState(share.title);
  const [editExpDate, setEditExpDate] = useState(share.expiration_date);
  const [editDirty, setEditDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [docSearch, setDocSearch] = useState("");
  const [docResults, setDocResults] = useState([]);
  const [docSearching, setDocSearching] = useState(false);
  const [docDropOpen, setDocDropOpen] = useState(false);
  const [addItemType, setAddItemType] = useState(null);
  const [addItemContent, setAddItemContent] = useState("");
  const [addingItem, setAddingItem] = useState(false);
  const docSearchRef = useRef(null);

  // Sync fields when share changes
  useEffect(() => {
    setEditTitle(share.title);
    setEditExpDate(share.expiration_date);
    setEditDirty(false);
    setDocSearch(""); setDocResults([]); setDocDropOpen(false);
    setAddItemType(null); setAddItemContent("");
  }, [share.id]);

  const allItems = share.documents || [];
  const docCount = allItems.filter(d => d.item_type === "document").length;

  useEffect(() => {
    if (!docSearch.trim()) { setDocResults([]); return; }
    const t = setTimeout(async () => {
      setDocSearching(true);
      try {
        const r = await api.get("/documents/search", { params: { q: docSearch, status: "classified" } });
        const existingDocIds = new Set(allItems.filter(d => d.item_type === "document").map(d => d.document_id));
        setDocResults((r.data || []).filter(d => !existingDocIds.has(d.id)));
      } catch (_) {}
      finally { setDocSearching(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [docSearch, share.documents]);

  useEffect(() => {
    const h = e => { if (docSearchRef.current && !docSearchRef.current.contains(e.target)) setDocDropOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const update = (patch) => setShares(p => p.map(s => s.id === selectedId ? { ...s, ...patch } : s));

  const saveEdits = async () => {
    setSaving(true);
    try {
      const r = await api.patch(`/shares/${selectedId}`, { title: editTitle, expiration_date: editExpDate });
      update(r.data); setEditDirty(false); toast.success("Enregistré.");
    } catch (err) { toast.error(getErrorMessage(err)); }
    finally { setSaving(false); }
  };

  const activate = async () => {
    setActionLoading(true);
    try { const r = await api.post(`/shares/${selectedId}/activate`); update(r.data); toast.success("Activé."); }
    catch (err) { toast.error(getErrorMessage(err)); }
    finally { setActionLoading(false); }
  };

  const deactivate = async () => {
    setActionLoading(true);
    try { const r = await api.post(`/shares/${selectedId}/deactivate`); update(r.data); toast.success("Désactivé."); }
    catch (err) { toast.error(getErrorMessage(err)); }
    finally { setActionLoading(false); }
  };

  const deleteShare = async () => {
    const msg = share.in_collection
      ? "Supprimer ce partage ? Il sera également retiré de toute collection qui l'inclut."
      : "Supprimer ce partage ?";
    if (!window.confirm(msg)) return;
    setActionLoading(true);
    try {
      await api.delete(`/shares/${selectedId}`);
      setShares(p => p.filter(s => s.id !== selectedId)); onClose();
      toast.success("Supprimé.");
    } catch (err) { toast.error(getErrorMessage(err)); }
    finally { setActionLoading(false); }
  };

  const addDocument = async (doc) => {
    try {
      const r = await api.post(`/shares/${selectedId}/documents`, { document_id: doc.id });
      update({ documents: r.data.documents, document_count: r.data.documents.filter(d => d.item_type === "document").length });
      setDocSearch(""); setDocResults([]); setDocDropOpen(false);
    } catch (err) { toast.error(getErrorMessage(err)); }
  };

  const addTextItem = async () => {
    if (!addItemContent.trim() || !addItemType) return;
    setAddingItem(true);
    try {
      const r = await api.post(`/shares/${selectedId}/items`, { item_type: addItemType, content: addItemContent.trim() });
      update({ documents: r.data.documents });
      setAddItemType(null); setAddItemContent("");
    } catch (err) { toast.error(getErrorMessage(err)); }
    finally { setAddingItem(false); }
  };

  const updateTextItem = async (itemId, content) => {
    try {
      const r = await api.patch(`/shares/${selectedId}/items/${itemId}`, { content });
      update({ documents: r.data.documents });
    } catch (err) { toast.error(getErrorMessage(err)); }
  };

  const removeItem = async (itemId) => {
    const item = allItems.find(d => d.id === itemId);
    try {
      await api.delete(`/shares/${selectedId}/documents/${itemId}`);
      update({
        documents: allItems.filter(d => d.id !== itemId),
        document_count: item?.item_type === "document" ? Math.max(0, (share.document_count || 1) - 1) : share.document_count,
      });
    } catch (err) { toast.error(getErrorMessage(err)); }
  };

  const copyLink = () => {
    const url = `${window.location.origin}/share/${share.token}`;
    navigator.clipboard.writeText(url).then(() => toast.success("Lien copié !"));
  };

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const handleDragEnd = useCallback(async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const docs = share?.documents || [];
    const oldIdx = docs.findIndex(d => d.id === active.id);
    const newIdx = docs.findIndex(d => d.id === over.id);
    if (oldIdx === -1 || newIdx === -1) return;
    const reordered = arrayMove(docs, oldIdx, newIdx);
    update({ documents: reordered });
    try { await api.put(`/shares/${selectedId}/documents/reorder`, { item_ids: reordered.map(d => d.id) }); }
    catch (err) { toast.error(getErrorMessage(err)); update({ documents: docs }); }
  }, [selectedId, share?.documents]);

  return (
    <div className="flex flex-col h-full bg-white" data-testid="share-detail-panel">
      {/* Header */}
      <div className="px-6 py-4 border-b border-zinc-200 flex items-center gap-4 shrink-0">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <StatusBadge status={share.status} />
            {share.first_activated_at && <span className="text-[10px] font-mono text-zinc-400">1ère activation : {new Date(share.first_activated_at).toLocaleDateString("fr-FR")}</span>}
          </div>
          <input data-testid="edit-share-title" type="text" value={editTitle}
            onChange={e => { setEditTitle(e.target.value); setEditDirty(true); }}
            className="text-xl font-semibold w-full border-b border-transparent focus:border-[#002FA7] outline-none pb-0.5 bg-transparent" />
        </div>
        <div className="shrink-0 text-right">
          <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 font-mono block mb-1">Expiration</label>
          <input data-testid="edit-share-exp-date" type="date" value={editExpDate} min={todayISO()}
            onChange={e => { setEditExpDate(e.target.value); setEditDirty(true); }}
            className="bg-white border border-zinc-300 px-2 py-1.5 text-sm focus:border-[#002FA7] outline-none" />
        </div>
        <button onClick={onClose} className="p-1.5 text-zinc-400 hover:text-zinc-700 shrink-0" data-testid="close-share-panel"><X size={16} /></button>
      </div>

      <div className="flex-1 overflow-y-auto app-scroll px-6 py-5 space-y-5">
        {editDirty && (
          <button data-testid="save-share-btn" onClick={saveEdits} disabled={saving}
            className="inline-flex items-center gap-2 text-sm font-medium text-white bg-[#002FA7] hover:bg-[#00227A] px-4 py-2 transition-colors disabled:opacity-50">
            {saving ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} Enregistrer
          </button>
        )}

        {share.first_activated_at && (
          <div className="flex items-center gap-2 flex-wrap">
            <code className="flex-1 min-w-0 text-xs font-mono bg-zinc-50 border border-zinc-200 px-3 py-2 truncate text-zinc-600">
              {window.location.origin}/share/{share.token}
            </code>
            <button data-testid="copy-share-link-btn" onClick={copyLink}
              className="shrink-0 inline-flex items-center gap-1.5 text-sm font-medium border border-[#002FA7] text-[#002FA7] hover:bg-[#002FA7] hover:text-white px-3 py-2 transition-colors whitespace-nowrap">
              <Link size={12} /> Copier le lien
            </button>
            {share.status === "active" && (
              <button data-testid="deactivate-share-btn" onClick={deactivate} disabled={actionLoading}
                className="shrink-0 inline-flex items-center gap-2 text-sm border border-amber-400 text-amber-700 hover:bg-amber-50 px-3 py-2 transition-colors disabled:opacity-50">
                {actionLoading ? <Loader2 size={13} className="animate-spin" /> : <Ban size={13} />} Désactiver
              </button>
            )}
            {share.status === "inactive" && (
              <button data-testid="activate-share-btn" onClick={activate} disabled={actionLoading}
                className="shrink-0 inline-flex items-center gap-2 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 px-3 py-2 transition-colors disabled:opacity-50">
                {actionLoading ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle size={13} />} Réactiver
              </button>
            )}
          </div>
        )}

        {share.status === "draft" && (
          <div className="flex gap-2 flex-wrap pt-2 border-t border-zinc-100">
            <button data-testid="activate-share-btn" onClick={activate} disabled={actionLoading}
              className="inline-flex items-center gap-2 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 px-4 py-2 transition-colors disabled:opacity-50">
              {actionLoading ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle size={13} />} Activer le partage
            </button>
          </div>
        )}

        <div className="pt-2 border-t border-zinc-100">
          <button data-testid="delete-share-btn" onClick={deleteShare} disabled={actionLoading}
            className="inline-flex items-center gap-2 text-sm border border-zinc-200 text-zinc-500 hover:border-red-300 hover:text-red-600 px-4 py-2 transition-colors disabled:opacity-50">
            {actionLoading ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />} Supprimer ce partage
          </button>
        </div>

        {/* Contenu */}
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 font-mono mb-3">
            Contenu · {allItems.length} élément(s) · {docCount} fichier(s)
          </div>

          {/* Recherche document */}
          <div ref={docSearchRef} className="relative mb-2">
            <div className="relative">
              <Search size={13} strokeWidth={1.5} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
              <input data-testid="add-doc-search" type="text" value={docSearch}
                onChange={e => { setDocSearch(e.target.value); setDocDropOpen(true); }}
                onFocus={() => docSearch && setDocDropOpen(true)}
                placeholder="Rechercher un document classifié à ajouter…"
                className="w-full bg-white border border-zinc-300 pl-8 pr-3 py-2 text-sm focus:border-[#002FA7] outline-none" autoComplete="off" />
              {docSearching && <Loader2 size={12} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 animate-spin" />}
            </div>
            {docDropOpen && docResults.length > 0 && (
              <div className="absolute z-20 w-full bg-white border border-zinc-300 border-t-0 shadow-lg max-h-48 overflow-y-auto app-scroll">
                {docResults.map(d => (
                  <button key={d.id} type="button" onMouseDown={e => { e.preventDefault(); addDocument(d); }}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-[#e6f1ff] transition-colors border-b border-zinc-50 flex items-start gap-2">
                    <FileText size={13} strokeWidth={1.5} className="text-[#DC2626] mt-0.5 shrink-0" />
                    <div className="min-w-0">
                      <div className="truncate">{d.classified_filename || d.original_filename}</div>
                      {d.classified_path && <div className="text-[10px] font-mono text-zinc-400 truncate">{d.classified_path}</div>}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Boutons Section/Commentaire */}
          <div className="flex gap-2 mb-3">
            {["section", "comment"].map(t => (
              <button key={t} data-testid={`add-${t}-btn`}
                onClick={() => { setAddItemType(addItemType === t ? null : t); setAddItemContent(""); }}
                className={`inline-flex items-center gap-1 text-xs border px-2 py-1.5 transition-colors ${addItemType === t ? "border-[#002FA7] text-[#002FA7] bg-[#002FA7]/5" : "border-zinc-200 text-zinc-500 hover:border-zinc-400"}`}>
                {t === "section" ? <Type size={11} strokeWidth={1.5} /> : <MessageSquare size={11} strokeWidth={1.5} />}
                + {t === "section" ? "Section" : "Commentaire"}
              </button>
            ))}
          </div>

          {addItemType && (
            <div className="mb-3 flex gap-2">
              <input autoFocus data-testid="add-text-item-input" type="text" value={addItemContent}
                onChange={e => setAddItemContent(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter") addTextItem(); if (e.key === "Escape") setAddItemType(null); }}
                placeholder={addItemType === "section" ? "Titre de la section…" : "Texte du commentaire…"}
                className="flex-1 border border-zinc-300 px-3 py-2 text-sm focus:border-[#002FA7] outline-none" />
              <button data-testid="add-text-item-submit" onClick={addTextItem} disabled={addingItem || !addItemContent.trim()}
                className="inline-flex items-center gap-1 text-xs font-medium text-white bg-[#002FA7] hover:bg-[#00227A] px-3 py-2 transition-colors disabled:opacity-50">
                {addingItem ? <Loader2 size={11} className="animate-spin" /> : <Check size={11} />} Ajouter
              </button>
              <button onClick={() => setAddItemType(null)} className="p-2 text-zinc-400 hover:text-zinc-700"><X size={13} /></button>
            </div>
          )}

          {allItems.length === 0 ? (
            <div className="text-sm text-zinc-400 py-3 text-center border border-dashed border-zinc-200">Aucun élément.</div>
          ) : (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={allItems.map(d => d.id)} strategy={verticalListSortingStrategy}>
                <div className="border border-zinc-200 overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-zinc-200 bg-zinc-50 text-[10px] uppercase tracking-[0.15em] font-mono text-zinc-500">
                        <th className="w-8 pl-3"></th>
                        <th className="px-3 py-2 text-left">Contenu</th>
                        <th className="px-3 py-2 text-right whitespace-nowrap">Pages</th>
                        <th className="px-3 py-2 text-right whitespace-nowrap">Taille</th>
                        <th className="px-3 py-2 w-28"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-100">
                      {allItems.map(d => d.item_type === "document"
                        ? <SortableDocRow key={d.id} doc={d} onRemove={removeItem} />
                        : <SortableTextRow key={d.id} item={d} onRemove={removeItem} onUpdate={updateTextItem} />
                      )}
                    </tbody>
                  </table>
                </div>
              </SortableContext>
            </DndContext>
          )}
        </div>

        <div className="pt-4 border-t border-zinc-100 text-[10px] font-mono text-zinc-400 space-y-1">
          <div>Créé le {new Date(share.created_at).toLocaleString("fr-FR")}</div>
          <div>Modifié le {new Date(share.updated_at).toLocaleString("fr-FR")}</div>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// Panneau Collection
// ══════════════════════════════════════════════════════════════════════════════

function CollectionPanel({ collection, collections, setCollections, shares, onClose }) {
  const collId = collection.id;
  const [editTitle, setEditTitle] = useState(collection.title);
  const [editExp, setEditExp] = useState(collection.expiration_date);
  const [editDirty, setEditDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [showAddShare, setShowAddShare] = useState(false);

  useEffect(() => {
    setEditTitle(collection.title);
    setEditExp(collection.expiration_date);
    setEditDirty(false);
  }, [collection.id]);

  const update = (patch) => setCollections(p => p.map(c => c.id === collId ? { ...c, ...patch } : c));

  const saveEdits = async () => {
    setSaving(true);
    try {
      const r = await api.patch(`/collections/${collId}`, { title: editTitle, expiration_date: editExp });
      update(r.data); setEditDirty(false); toast.success("Enregistré.");
    } catch (err) { toast.error(getErrorMessage(err)); }
    finally { setSaving(false); }
  };

  const activate = async () => {
    setActionLoading(true);
    try { const r = await api.post(`/collections/${collId}/activate`); update(r.data); toast.success("Collection activée."); }
    catch (err) { toast.error(getErrorMessage(err)); }
    finally { setActionLoading(false); }
  };

  const deactivate = async () => {
    setActionLoading(true);
    try { const r = await api.post(`/collections/${collId}/deactivate`); update(r.data); toast.success("Collection désactivée."); }
    catch (err) { toast.error(getErrorMessage(err)); }
    finally { setActionLoading(false); }
  };

  const deleteCollection = async () => {
    const count = collShares.length;
    const msg = count > 0
      ? `Supprimer cette collection ? Les ${count} dossier(s) inclus ne seront pas supprimés — ils redeviendront libres.`
      : "Supprimer cette collection ?";
    if (!window.confirm(msg)) return;
    setActionLoading(true);
    try {
      await api.delete(`/collections/${collId}`);
      setCollections(p => p.filter(c => c.id !== collId)); onClose(); toast.success("Supprimée.");
    } catch (err) { toast.error(getErrorMessage(err)); }
    finally { setActionLoading(false); }
  };

  const removeShare = async (shareId) => {
    try {
      await api.delete(`/collections/${collId}/shares/${shareId}`);
      update({ shares: (collection.shares || []).filter(s => s.id !== shareId), share_count: (collection.share_count || 1) - 1 });
    } catch (err) { toast.error(getErrorMessage(err)); }
  };

  const addShare = async (share) => {
    try {
      const r = await api.post(`/collections/${collId}/shares`, { share_id: share.id });
      update({ shares: r.data.shares, share_count: r.data.shares.length });
      setShowAddShare(false);
    } catch (err) { toast.error(getErrorMessage(err)); }
  };

  const copyLink = () => {
    const url = `${window.location.origin}/bundle/${collection.token}`;
    navigator.clipboard.writeText(url).then(() => toast.success("Lien copié !"));
  };

  const collShares = collection.shares || [];
  const collShareIds = new Set(collShares.map(s => s.id));
  const availableShares = shares.filter(s => !collShareIds.has(s.id) && s.status !== "expired");

  return (
    <div className="flex flex-col h-full bg-white" data-testid="collection-detail-panel">
      {/* Header */}
      <div className="px-6 py-4 border-b border-zinc-200 flex items-center gap-4 shrink-0">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[10px] font-mono text-zinc-400 uppercase tracking-[0.15em]">Collection</span>
            <StatusBadge status={collection.status} />
          </div>
          <input data-testid="edit-coll-title" type="text" value={editTitle}
            onChange={e => { setEditTitle(e.target.value); setEditDirty(true); }}
            className="text-xl font-semibold w-full border-b border-transparent focus:border-[#002FA7] outline-none pb-0.5 bg-transparent" />
        </div>
        <div className="shrink-0 text-right">
          <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 font-mono block mb-1">Expiration</label>
          <input data-testid="edit-coll-exp" type="date" value={editExp} min={todayISO()}
            onChange={e => { setEditExp(e.target.value); setEditDirty(true); }}
            className="bg-white border border-zinc-300 px-2 py-1.5 text-sm focus:border-[#002FA7] outline-none" />
        </div>
        <button onClick={onClose} className="p-1.5 text-zinc-400 hover:text-zinc-700 shrink-0"><X size={16} /></button>
      </div>

      <div className="flex-1 overflow-y-auto app-scroll px-6 py-5 space-y-5">
        {editDirty && (
          <button onClick={saveEdits} disabled={saving} data-testid="save-coll-btn"
            className="inline-flex items-center gap-2 text-sm font-medium text-white bg-[#002FA7] hover:bg-[#00227A] px-4 py-2 transition-colors disabled:opacity-50">
            {saving ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} Enregistrer
          </button>
        )}

        {/* Lien bundle */}
        {collection.first_activated_at && (
          <div className="flex items-center gap-2 flex-wrap">
            <code className="flex-1 min-w-0 text-xs font-mono bg-zinc-50 border border-zinc-200 px-3 py-2 truncate text-zinc-600">
              {window.location.origin}/bundle/{collection.token}
            </code>
            <button onClick={copyLink} data-testid="copy-bundle-link-btn"
              className="shrink-0 inline-flex items-center gap-1.5 text-sm font-medium border border-[#002FA7] text-[#002FA7] hover:bg-[#002FA7] hover:text-white px-3 py-2 transition-colors whitespace-nowrap">
              <Link size={12} /> Copier le lien
            </button>
            {collection.status === "active" && (
              <button onClick={deactivate} disabled={actionLoading} data-testid="deactivate-coll-btn"
                className="shrink-0 inline-flex items-center gap-2 text-sm border border-amber-400 text-amber-700 hover:bg-amber-50 px-3 py-2 transition-colors disabled:opacity-50 whitespace-nowrap">
                {actionLoading ? <Loader2 size={13} className="animate-spin" /> : <Ban size={13} />} Désactiver
              </button>
            )}
            {collection.status === "inactive" && (
              <button onClick={activate} disabled={actionLoading} data-testid="activate-coll-btn"
                className="shrink-0 inline-flex items-center gap-2 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 px-3 py-2 transition-colors disabled:opacity-50 whitespace-nowrap">
                {actionLoading ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle size={13} />} Réactiver
              </button>
            )}
          </div>
        )}

        {collection.status === "draft" && (
          <div className="flex gap-2 flex-wrap pt-2 border-t border-zinc-100">
            <button onClick={activate} disabled={actionLoading} data-testid="activate-coll-btn"
              className="inline-flex items-center gap-2 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 px-4 py-2 transition-colors disabled:opacity-50">
              {actionLoading ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle size={13} />} Activer la collection
            </button>
          </div>
        )}

        <div className="pt-2 border-t border-zinc-100">
          <button onClick={deleteCollection} disabled={actionLoading} data-testid="delete-coll-btn"
            className="inline-flex items-center gap-2 text-sm border border-zinc-200 text-zinc-500 hover:border-red-300 hover:text-red-600 px-4 py-2 transition-colors disabled:opacity-50">
            {actionLoading ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />} Supprimer cette collection
          </button>
        </div>

        {/* Dossiers inclus */}
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 font-mono mb-3 flex items-center justify-between">
            <span>Dossiers inclus · {collShares.length}</span>
            <button onClick={() => setShowAddShare(v => !v)} data-testid="add-share-to-coll-btn"
              className="inline-flex items-center gap-1 text-xs border border-zinc-200 text-zinc-600 hover:border-zinc-400 px-2 py-1 transition-colors">
              <Plus size={11} strokeWidth={2} /> Ajouter un dossier
            </button>
          </div>

          {showAddShare && availableShares.length > 0 && (
            <div className="mb-3 border border-zinc-200 bg-zinc-50 max-h-48 overflow-y-auto app-scroll">
              {availableShares.map(s => (
                <button key={s.id} onMouseDown={e => { e.preventDefault(); addShare(s); }}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-[#e6f1ff] border-b border-zinc-100 flex items-center gap-2 transition-colors">
                  <Share2 size={13} strokeWidth={1.5} className="text-zinc-400 shrink-0" />
                  <span className="flex-1 truncate">{s.title}</span>
                  <StatusBadge status={s.status} />
                </button>
              ))}
            </div>
          )}
          {showAddShare && availableShares.length === 0 && (
            <div className="mb-3 text-xs text-zinc-400 py-2">Tous les partages actifs sont déjà inclus.</div>
          )}

          {collShares.length === 0 ? (
            <div className="text-sm text-zinc-400 py-3 text-center border border-dashed border-zinc-200">
              Aucun dossier inclus. Ajoutez des partages existants.
            </div>
          ) : (
            <div className="border border-zinc-200 divide-y divide-zinc-100">
              {collShares.map((s, i) => (
                <div key={s.id} className="flex items-center gap-3 px-3 py-2.5 hover:bg-zinc-50">
                  <span className="text-[10px] font-mono text-zinc-300 w-4 shrink-0">{i + 1}</span>
                  <Share2 size={13} strokeWidth={1.5} className="text-zinc-400 shrink-0" />
                  <span className="flex-1 text-sm truncate">{s.title}</span>
                  <StatusBadge status={s.status} />
                  <button onClick={() => removeShare(s.id)}
                    className="text-zinc-300 hover:text-red-500 transition-colors p-1">
                    <X size={13} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="pt-4 border-t border-zinc-100 text-[10px] font-mono text-zinc-400 space-y-1">
          <div>Créé le {new Date(collection.created_at).toLocaleString("fr-FR")}</div>
          <div>Modifié le {new Date(collection.updated_at).toLocaleString("fr-FR")}</div>
        </div>
      </div>
    </div>
  );
}
